import { useEffect, useLayoutEffect, useRef, useState } from "react";

import { useAnnounce } from "../app/announcer";
import { useRouter } from "../app/router";
import { selectDirection, useStudio } from "../app/store";
import { DirectionCardView } from "./DirectionCard";
import { ExportWrap } from "./ExportWrap";
import { JourneyChoice } from "./JourneyChoice";
import { PreviewLab } from "./PreviewLab";
import { Processing } from "./Processing";
import { QuestionFlow, type BriefPageProgress } from "./QuestionFlow";
import { WordsStep } from "./WordsStep";

const progressFor = (deep: boolean, step: string, page: BriefPageProgress | null) => {
  const total = (deep ? 4 : 2) + 3;
  if (["direction", "lab", "export"].includes(step)) return { current: total, total, label: "Your direction" };
  if (step === "path") return { current: 1, total, label: "Choose your depth" };
  if (step === "brief") return { current: Math.min(total - 2, (page?.page ?? 0) + 2), total, label: page?.title ?? "About the deck" };
  if (step === "words") return { current: total - 1, total, label: "Your slide" };
  return { current: total - 1, total, label: "Making your direction" };
};

export const Studio = () => {
  const { state, dispatch } = useStudio();
  const { navigate } = useRouter();
  const announce = useAnnounce();
  const mainRef = useRef<HTMLDivElement>(null);
  const [briefPage, setBriefPage] = useState<BriefPageProgress | null>(null);
  const direction = selectDirection(state);
  const deep = state.brief.typePath === "deep";

  useEffect(() => {
    if (state.entry !== "type") navigate("/");
  }, [state.entry, navigate]);

  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    const frame = window.requestAnimationFrame(() => window.scrollTo({ top: 0, left: 0, behavior: "auto" }));
    const heading = mainRef.current?.querySelector<HTMLElement>("h2");
    if (heading) {
      heading.tabIndex = -1;
      heading.focus({ preventScroll: true });
    }
    return () => window.cancelAnimationFrame(frame);
  }, [state.step]);

  useEffect(() => {
    if (state.step !== "brief") setBriefPage(null);
  }, [state.step]);

  if (state.entry !== "type") return null;
  const progress = progressFor(deep, state.step, briefPage);
  const remaining = progress.total - progress.current;
  const remainingLabel = remaining === 0 ? "Done" : remaining === 1 ? "One step left" : `${remaining} steps left`;

  const choosePath = (path: "quick" | "deep") => {
    dispatch({ type: "setBrief", brief: { ...state.brief, route: "type", typePath: path } });
    dispatch({ type: "setStep", step: "brief" });
    announce(path === "deep" ? "Long ride chosen. Every remaining step stays visible." : "Short ride chosen. Two useful question pages.");
  };

  return (
    <div ref={mainRef} className="studio-shell">
      <header className="journey-header">
        <div className="journey-meta"><span className="journey-route">Type Studio</span><span className="journey-count">Step {progress.current} of {progress.total}</span></div>
        <div className="journey-label-row"><strong>{progress.label}</strong><span>{remainingLabel}</span></div>
        <div className="journey-track" aria-label={`Step ${progress.current} of ${progress.total}`} role="progressbar" aria-valuemin={1} aria-valuemax={progress.total} aria-valuenow={progress.current}>
          {Array.from({ length: progress.total }, (_, index) => <span key={index} data-state={index + 1 < progress.current ? "done" : index + 1 === progress.current ? "current" : "upcoming"} />)}
        </div>
      </header>

      <div className="studio-page">
        {state.step === "path" ? <JourneyChoice entry="type" onChoose={choosePath} onBack={() => navigate("/")} /> : null}
        {state.step === "brief" ? <QuestionFlow onDone={() => { dispatch({ type: "setStep", step: "words" }); announce("Next: try the direction with one real slide."); }} onProgress={setBriefPage} onBack={() => dispatch({ type: "setStep", step: "path" })} /> : null}
        {state.step === "words" ? <WordsStep onDone={() => dispatch({ type: "setStep", step: "processing" })} onBack={() => dispatch({ type: "setStep", step: "brief" })} /> : null}
        {state.step === "processing" ? <Processing onCancelled={() => dispatch({ type: "setStep", step: "words" })} /> : null}
        {state.step === "direction" && direction ? <DirectionCardView direction={direction} onOpenLab={() => dispatch({ type: "setStep", step: "lab" })} onOpenBench={() => undefined} onOpenExport={() => dispatch({ type: "setStep", step: "export" })} /> : null}
        {state.step === "lab" ? <><button type="button" className="back-to-result" onClick={() => dispatch({ type: "setStep", step: "direction" })}>← Back to my result</button><PreviewLab /></> : null}
        {state.step === "export" ? <><button type="button" className="back-to-result" onClick={() => dispatch({ type: "setStep", step: "direction" })}>← Back to my result</button><ExportWrap /></> : null}
      </div>
    </div>
  );
};
